/**
 * Learning Materials API Client
 * -----------------------------
 * Handles fetching learning content for section groups from MongoDB via REST API.
 */

import { firebaseAuth } from './firebase.js';
import { decryptResponse } from './crypto.js';

const API_BASE = import.meta.env.VITE_API_BASE;

/**
 * Helper for fetch with error handling
 */
async function apiFetch(path, options = {}) {
  const user = firebaseAuth.currentUser;
  const token = user ? await user.getIdToken() : null;
  const url = `${API_BASE}${path}`;
  const response = await fetch(url, {
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers
    },
    ...options
  });

  if (!response.ok) {
    const error = new Error(`API error: ${response.status} ${response.statusText}`);
    error.status = response.status;
    throw error;
  }

  return response.json().then(decryptResponse);
}

export const learningMaterialsApi = {
  /**
   * Fetch learning material for a single section group
   * @param {string} sectionGroupId
   * @returns {Promise<Object|null>} Material object or null if not found
   */
  async getMaterial(sectionGroupId) {
    try {
      const { material } = await apiFetch(`/learning-materials/${encodeURIComponent(sectionGroupId)}`);
      return material || null;
    } catch (err) {
      if (err.status === 404) {
        return null;
      }
      throw err;
    }
  },

  /**
   * Fetch learning materials for multiple section groups (batch)
   * @param {string[]} sectionGroupIds
   * @returns {Promise<Object>} Map of sectionGroupId -> material
   */
  async getMaterials(sectionGroupIds) {
    if (!sectionGroupIds.length) return {};

    const ids = sectionGroupIds.join(',');
    const { materials } = await apiFetch(`/learning-materials?sectionGroupIds=${encodeURIComponent(ids)}`);
    return materials || {};
  },

  /**
   * Fetch all materials for a lesson's subsections
   * @param {Object} lesson - Lesson object with subsections array
   * @returns {Promise<Object>} Map of sectionGroupId -> material
   */
  async getMaterialsForLesson(lesson) {
    const sgIds = (lesson.subsections || lesson.formatSectionGroups || [])
      .map(sub => typeof sub === 'string' ? sub : sub.content)
      .filter(Boolean);

    if (!sgIds.length) return {};
    return this.getMaterials(sgIds);
  },

  /**
   * Check if a section group has learning content available
   * @param {string} sectionGroupId
   * @returns {Promise<boolean>}
   */
  async hasContent(sectionGroupId) {
    const material = await this.getMaterial(sectionGroupId);
    return material !== null;
  },

  /**
   * Get all available learning materials
   * @returns {Promise<Object>} Map of sectionGroupId -> material
   */
  async getAllMaterials() {
    const { materials } = await apiFetch('/learning-materials');
    return materials || {};
  },

  /**
   * Get available section group IDs that have content
   * @returns {Promise<string[]>}
   */
  async getAvailableSectionGroups() {
    const materials = await this.getAllMaterials();
    return Object.keys(materials);
  },

  /**
   * Save a single learning material (admin use)
   * @param {Object} material - Material object with sectionGroupId
   * @returns {Promise<Object>} {success: true, sectionGroupId: string}
   */
  async saveMaterial(material) {
    return apiFetch('/learning-materials', {
      method: 'POST',
      body: JSON.stringify(material)
    });
  },

  /**
   * Save multiple learning materials (batch, admin use)
   * @param {Object} materials - Map of sectionGroupId -> material
   * @returns {Promise<Object>} {success: true, saved: number}
   */
  async saveMaterials(materials) {
    return apiFetch('/learning-materials', {
      method: 'POST',
      body: JSON.stringify({ materials })
    });
  }
};

export default learningMaterialsApi;
